<template>
    <div class="relative w-full overflow-hidden" :class="{ 'h-screen': !campagneId, 'h-full': campagneId }">
      <div id="google-map" ref="mapRef" class="w-full h-full rounded-lg shadow-md"></div>
  
      <div class="absolute top-40 bg-white p-3 w-40 left-4 rounded-lg shadow-md z-1">
        <div v-if="routes" class="rounded-sm max-h-40 overflow-y-auto">
            <h2 class="text-xl font-semibold mb-2">Trajets</h2>
            <button
                v-if="(routes.length > 2)"
                @click="displayAllRoutes()"
                :class="[
                    'px-1 py-1 rounded mt-2 w-full transition-colors text-sm flex items-center',
                    showAllRoutes ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300',
                ]"
            >
            <span class="inline-block w-4 h-4 rounded-full mr-2">Tout</span>
            </button>
            <button
            v-for="(route, routeIndex) in routes"
                :key="`route-${routeIndex}`"
                @click="selectRoute(routeIndex)"
                :class="[
                    'px-1 py-1 rounded mt-2 w-full transition-colors text-sm flex items-center',
                    selectedRouteIndex === routeIndex ? 'bg-blue-600 text-white' : 'bg-gray-200 hover:bg-gray-300',
                ]"
            >
            <span class="inline-block w-4 h-4 rounded-full mr-2" :style="{ backgroundColor: route.color }"></span>
            {{ route.name }}
            </button>
        </div>
      </div>
      <div v-if="errorMessage" class="absolute top-4 left-4 bg-red-200 text-red-800 p-2 rounded shadow-md">
        {{ errorMessage }}
      </div>
    </div>
  </template>
  
  <script>
  import { Loader } from "@googlemaps/js-api-loader";
  
  export default {
    name: 'GoogleMapsComponent',
    props: {
      selectedDate: {
        type: String,
        default: () => new Date().toISOString().slice(0, 10),
      },
      campagneId: {
        type: String,
        default: null,
      },
      routes:{
        type: Array,
        default: null,
      }
    },
    data() {
      return {
        googleMapsApiKey: 'AIzaSyCR2zyPaTlEdVvAnmETWlMx-LDw0ypIDNw', // Remplacez par ta clé API
        selectedRouteIndex: 0,
        map: null,
        directionsService: null,
        directionsRenderer: null,
        errorMessage: "",
        showAllRoutes: false,  // Nouvel état pour suivre si "Tout" est sélectionné
        renderers: [], 
      };
    },
    computed: {
      currentRoutePoints() {
        if (this.selectedRouteIndex === -1 || !this.routes || this.routes.length === 0) {
        // Si "Tout" est sélectionné ou pas de routes disponibles, retourner le premier point de la première route
        return this.routes && this.routes.length > 0 ? this.routes[0].points : [];
        }
        return this.routes[this.selectedRouteIndex].points;
      },
    },
    mounted() {
        console.log('Route', this.routes)
      this.initMap();
    },
    methods: {
      /* global */

        /**
         * Affiche toutes les routes sur la carte simultanément
         */
        displayAllRoutes() {
            this.showAllRoutes = true;
            this.selectedRouteIndex = -1; // -1 indique qu'aucune route individuelle n'est sélectionnée

            // Nettoyer les renderers précédents
            if (this.directionsRenderer) {
                this.directionsRenderer.setMap(null);
            }

            this.renderers.forEach(renderer => {
                renderer.setMap(null);
            });
            this.renderers = [];

            // Créer un renderer pour chaque route
            this.routes.forEach((route, index) => {
            if (!route || route.points.length < 2) {
                return;
            }

            const waypoints = route.points.slice(1, -1).map(point => ({
                location: point.position,
                stopover: true,
            }));

            const request = {
                origin: route.points[0].position,
                destination: route.points[route.points.length - 1].position,
                waypoints: waypoints,
                optimizeWaypoints: true,
                travelMode: google.maps.TravelMode.DRIVING,
            };

            const renderer = new google.maps.DirectionsRenderer({
                map: this.map,
                suppressMarkers: true,
                polylineOptions: {
                strokeColor: route.color,
                strokeWeight: 5,
                },
            });

            this.renderers.push(renderer);

            this.directionsService.route(request, (result, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                renderer.setDirections(result);
                
                // Ajouter les marqueurs pour cette route
                this.addRouteMarkers(route.points, index);
                
                // Ajuster les limites de la carte pour la première route seulement
                if (index === 0) {
                    this.fitBoundsToRoute(result);
                }
                } else {
                console.error("Erreur Directions pour la route", index, ":", status);
                }
            });
            });
        },

        /**
         * Ajoute des marqueurs pour une route spécifique
         */
        addRouteMarkers(points) {
        points.forEach((point, index) => {
        new google.maps.Marker({
            position: point.position,
            map: this.map,
            title: point.name,
            label: (index + 1).toString(),
            // Option: vous pouvez ajouter une icône personnalisée basée sur la couleur de la route
            // icon: { ... }
        });
        });
        },

      /**
       * Initialise la carte Google Maps de manière asynchrone.
       * Charge l'API Google Maps, crée la carte, les services de directions et affiche la route initiale.
       * Gère les erreurs potentielles lors du chargement de l'API.
       */
       async initMap() {
        try {
            const loader = new Loader({
                apiKey: this.googleMapsApiKey,
                libraries: ['routes', 'geometry'],
            });

            await loader.load();

            this.map = new google.maps.Map(this.$refs.mapRef, {
                center: this.currentRoutePoints[0].position,
                zoom: 6,
                mapTypeId: google.maps.MapTypeId.ROADMAP,
            });

            this.directionsService = new google.maps.DirectionsService();
            this.directionsRenderer = new google.maps.DirectionsRenderer({
                map: this.map,
                suppressMarkers: true,
            });

            // Si nous sommes en mode "Tout", afficher toutes les routes
            if (this.showAllRoutes) {
                this.displayAllRoutes();
            } else {
                this.displayRoute();
            }
        } catch (error) {
            console.error("Erreur lors du chargement de Google Maps :", error);
            this.errorMessage = "Erreur lors du chargement de la carte. Veuillez vérifier votre connexion et votre clé API.";
        }
       },
  
      /**
       * Affiche l'itinéraire sélectionné sur la carte.
       * Calcule l'itinéraire à l'aide du service de directions de Google Maps et l'affiche sur la carte.
       * Ajoute également des marqueurs pour chaque point de la route.
       */
      displayRoute() {
        const route = this.routes[this.selectedRouteIndex];
        if (!route || route.points.length < 2) {
          return;
        }
  
        const waypoints = route.points.slice(1, -1).map(point => ({
          location: point.position,
          stopover: true,
        }));
  
        const request = {
          origin: route.points[0].position,
          destination: route.points[route.points.length - 1].position,
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: google.maps.TravelMode.DRIVING,
        };
  
        this.directionsService.route(request, (result, status) => {
          if (status === google.maps.DirectionsStatus.OK) {
            this.directionsRenderer.setOptions({
              polylineOptions: {
                strokeColor: route.color,
                strokeWeight: 5,
              },
            });
            this.directionsRenderer.setDirections(result);
            this.fitBoundsToRoute(result);
            this.addMarkers(route.points);
          } else {
            console.error("Erreur Directions:", status);
            this.errorMessage = `Erreur lors du calcul de l'itinéraire : ${status}`;
          }
        });
      },
  
      /**
       * Ajoute des marqueurs à la carte pour chaque point de la route.
       * Supprime les marqueurs précédents et ajoute des nouveaux marqueurs.
       */
      addMarkers(points) {
        // Supprime les marqueurs précédents
        this.directionsRenderer.setOptions({
          suppressMarkers: false,
        });
  
        // Ajoute des nouveaux marqueurs
        points.forEach((point, index) => {
          new google.maps.Marker({
            position: point.position,
            map: this.map,
            title: point.name,
            label: (index + 1).toString(),
          });
        });
      },
  
      /**
       * Ajuste la vue de la carte pour afficher l'itinéraire complet.
       * Calcule les limites de la carte en fonction des points de l'itinéraire et ajuste la vue de la carte.
       */
      fitBoundsToRoute(result) {
        const bounds = new google.maps.LatLngBounds();
        const route = result.routes[0];
        route.legs.forEach(leg => {
          leg.steps.forEach(step => {
            step.path.forEach(path => {
              bounds.extend(path);
            });
          });
        });
        this.map.fitBounds(bounds);
      },
  
      /**
       * Sélectionne une route et affiche l'itinéraire correspondant sur la carte.
       */
       selectRoute(index) {
            this.showAllRoutes = false;
            this.selectedRouteIndex = index;

            // Nettoyer les renderers précédents si on affichait toutes les routes
            this.renderers.forEach(renderer => {
            renderer.setMap(null);
            });
            this.renderers = [];

            // Réinitialiser le renderer principal s'il existe
            if (this.directionsRenderer) {
                this.directionsRenderer.setMap(this.map);
            }

            this.displayRoute();
        },
    },
  };
  </script>